<template>
  <auth-form-wrapper
    :title="$t('auth.login.title')"
    :subtitle="$t('auth.login.subtitle')"
    :error="error"
    :community="communityData"
  >
    <b-form 
      class="auth-form mt-2" 
      @submit.prevent="handleSubmit"
      autocomplete="off"
    >
      <!-- Email -->
      <div class="mb-1">
        <label class="form-label">
          {{ $t('auth.login.form.email.label') }}
        </label>
        <b-form-input
          id="login-email"
          v-model="formData.email"
          type="email"
          name="email"
          :placeholder="$t('auth.login.form.email.placeholder')"
          required
        />
      </div>

      <!-- Password -->
      <div class="mb-1">
        <label class="form-label">
          {{ $t('auth.login.form.password.label') }}
        </label>
        <div class="input-group input-group-merge">
          <b-form-input
            id="login-password"
            v-model="formData.password"
            :type="passwordFieldType"
            name="password"
            :placeholder="$t('auth.login.form.password.placeholder')"
            class="form-control-merge"
            required
          />
          <div class="input-group-append">
            <div class="input-group-text cursor-pointer" @click="togglePasswordVisibility">
              <feather-icon
                :icon="passwordToggleIcon"
                size="16"
                class="toggle-password-icon"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- reCAPTCHA -->
      <div class="mb-1 d-flex justify-content-center">
        <vue-recaptcha
          ref="recaptcha"
          :sitekey="recaptchaSiteKey"
          @verify="onCaptchaVerified"
          @expired="onCaptchaExpired"
        />
      </div>

      <!-- Forgot Password Link -->
      <div v-if="showForgotPassword" class="mb-1">
        <span>{{ $t('auth.login.forgot-password.message') }}</span>
        <b-link
          class="auth-link"
          :to="forgotPasswordRoute"
          @click="trackLinkClick('login', 'restablecer contraseña')"
        >
          {{ $t('auth.login.forgot-password.action') }}
        </b-link>
      </div>

      <!-- Submit Button -->
      <div class="auth-btn-container d-flex justify-content-center">
        <b-button
          type="submit"
          variant="primary"
          class="auth-btn auth-btn-primary mt-2 mb-2"
          :disabled="submitButtonDisabled"
          @click="trackButtonClick('login', 'iniciar sesion')"
        >
          {{ $t('auth.login.form.action.sign-in') }}
        </b-button>
      </div>
    </b-form>

    <!-- <div v-if="showRecaptchaMessage" class="text-center mt-2">
      <p>reCAPTCHA is required!</p>
    </div> -->

    <!-- Sign Up Link -->
    <p v-if="showSignup" class="text-center mt-2">
      <span>{{ $t('auth.login.sign-up.message') }}</span>
      <b-link
        v-if="isExternal(signupRoute)"
        :href="signupRoute"
        class="auth-link"
        target="_blank"
        rel="noopener"
        @click="trackLinkClick('login', 'registrate')"
      >
        {{ $t('auth.login.sign-up.action') }}
      </b-link>
      <b-link
        v-else
        :to="signupRoute"
        class="auth-link"
        @click="trackLinkClick('login', 'registrate')"
      >
        {{ $t('auth.login.sign-up.action') }}
      </b-link>
    </p>

    <!-- SSO Buttons -->
    <div
      v-if="showOAuthButtons"
      class="text-center mt-2"
    >
      <p>· {{ $t("auth.login.form.action.sign-in-with") }} ·</p>
      
      <!-- Google Auth -->
      <b-button
        v-if="showGoogleButton"
        variant="outline-primary"
        :href="communityData.googleURL"
        :class="showSSObutton ? 'mr-3' : ''"
      >
        <b-icon-google />
      </b-button>

      <!-- Linkedin Auth -->

      <!-- SSO Auth -->
      <b-button
        v-if="showSSObutton"
        variant="outline-primary"
        :href="ssoURL"
      >
        SSO
      </b-button>

      <!-- SAML Auth (Nexos) -->
      <b-button
        v-if="showSAMLButton"
        variant="outline-primary"
        @click="handleSAMLLogin"
        :disabled="isSAMLLoading"
        :class="(showGoogleButton || showSSObutton) ? 'ml-3' : ''"
      >
        {{ isSAMLLoading ? $t('auth.login.saml.loading') : $t('auth.login.saml.button') }}
      </b-button>

      <!-- Nectios Auth -->
    </div>
  </auth-form-wrapper>
</template>

<script>
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { translateTranslationTable } from '@/@core/libs/i18n/utils'
import { getImageResource } from '@/@core/utils/image-utils'
import BrandLogo from '@/assets/images/brand/powerby-nectios.svg'
import Service from '@/config/service-identifiers'
import AuthFormWrapper from '@/auth/components/AuthFormWrapper.vue'
import { authHandler } from '@/auth/mixins/auth-handler'
import { isUserLoggedIn } from '@/auth/utils'
import VueRecaptcha from 'vue-recaptcha'
import { trackPageview, trackButtonClick, trackLinkClick } from '@/utils/analytics';

export default {
  name: 'Login',

  inject: {
    authApiUrl: {
      default: process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com/auth'
    },
    isIntercontinentalAlliance: {
      default: () => false
    },
    isNexos: {
      default: () => false
    },
    isCoeic: {
      default: () => false
    },
    isInhospitecnia: {
      default: () => false
    },
    isConexionEspana: {
      default: () => false
    },
    supportEmail: {
      default: () => process.env.VUE_APP_SUPPORT_EMAIL || 'support@nectios.com'
    },
    community: {
      default: () => null
    }
  },

  mixins: [togglePasswordVisibility, authHandler],

  components: {
    AuthFormWrapper,
    VueRecaptcha
  },

  data() {
    return {
      formData: {
        email: '',
        password: ''
      },
      error: null,
      invalid: false,
      oAuthApps: null,
      ssoURL: null,
      isLoading: true,
      communityInitialized: false,
      captchaVerified: false,
      captchaToken: '',
      recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
      isSAMLLoading: false
    }
  },

  computed: {
    communityData() {
      return this.community();
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    collective() {
      return this.$store.getters.currentCollective;
    },
    mainCollective() {
      return this.$store.getters.mainCollective;
    },
    mainSpace() {
      return this.mainCollective.key ? this.mainCollective : this.collective;
    },
    communitySlug() {
      const slugParam = this.$route.params.communityId;
      // Validate slug - if it's undefined or invalid, return an empty string instead
      if (!slugParam || slugParam === 'undefined' || slugParam === 'null') {
        return '';
      }
      return slugParam;
    },
    // Add computed properties for route names
    forgotPasswordRoute() {
      if(this.isInhospitecniaValue){
        return "https://hospitecnia.com/user/password/";
      }
      return { name: 'auth-forgot', params: { communityId: this.communitySlug } }
    },
    signupRoute() {
      if(this.isInhospitecniaValue){
        return "https://hospitecnia.com/signup/";
      }
      if(this.isConexionEspanaValue){
        return "https://conexionespana.online/?utm_source=mautic&utm_medium=email&utm_campaign=registro-enclave&utm_content=landing_web";
      }
      return { name: 'auth-signup', params: { communityId: this.communitySlug } }
    },
    showSignup() {
      if(this.isInhospitecniaValue){
        return false;
      }
      return this.communityData && this.communityData.accessibility < 2;
    },
    showForgotPassword() {
      if(this.isInhospitecniaValue){
        return false;
      }
      return true;
    },
    isInhospitecniaValue() {
      // Properly use the injected function by calling it
      return this.isInhospitecnia();
    },
    isCoeicValue() {
      // Properly use the injected function by calling it
      return this.isCoeic();
    },
    isConexionEspanaValue() {
      // Properly use the injected function by calling it
      return this.isConexionEspana();
    },
    showOAuthButtons() {
      return this.communitySlug === 'nectios-user-guide' || this.isCoeicValue ||
             (this.communityData && 
              this.communityData.googleURL && 
              this.oAuthApps && 
              this.oAuthApps.apps && 
              this.oAuthApps.apps['92']) || 
              this.communitySlug === 'barcelona-activa' ||
              this.showSAMLButton;
    },
    showGoogleButton() {
      return this.communityData && 
             this.communityData.googleURL && 
             this.oAuthApps && 
             this.oAuthApps.apps && 
             this.oAuthApps.apps['92']
    },
    showSSObutton() {
      return this.isCoeicValue;
    },
    showSAMLButton() {
      return this.isNexos() && this.communitySlug;
    },
    backgroundStyle() {
      if (!this.communityData || 
          !this.communityData.customization || 
          !this.communityData.customization.theme || 
          !this.communityData.customization.theme.login || 
          !this.communityData.customization.theme.login.backgrounds || 
          !this.communityData.customization.theme.login.backgrounds.length) {
        return null;
      }

      return {
        backgroundImage: `url(${getImageResource(this.communityData.customization.theme.login.backgrounds[0])})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat !important',
      };
    },
    communityLogo() {
      if (!this.communityData) return null;
      
      const logo = this.communityData.customization && 
                   this.communityData.customization.theme && 
                   this.communityData.customization.theme.login && 
                   this.communityData.customization.theme.login.logo;
      
      const logoURL = logo || 
                     (this.communityData.logoURL) || 
                     (this.communityData.header && this.communityData.header.logo_url);
      
      return logoURL ? getImageResource(logoURL) : null;
    },
    brand() {
      return BrandLogo;
    },
    landingURL() {
      return process.env.VUE_APP_LANDING_URL_COMMUNITY;
    },
    submitButtonDisabled() {
      return this.invalid || !this.captchaVerified;
    },
    showRecaptchaMessage() {
      return !this.captchaVerified;
    },
  },

  watch: {
    'formData.email'(newVal) {
      this.validateForm()
    },
    'formData.password'(newVal) {
      this.validateForm()
    },
    communityData: {
      immediate: true,
      handler(newVal) {
        // console.log('[Debug] Community data changed:', newVal);
        if (newVal && !this.communityInitialized) {
          this.communityInitialized = true;
          this.loadOAuthAndCustomization();
        }
      }
    }
  },

  async created() {

    // Push pageview event
    trackPageview('login', '/login');

    // Check if user is already logged in, redirect if they are
    if (isUserLoggedIn()) {
      this.redirectToMainPage();
      return;
    }

    // Show registration success message
    if (this.$route.query.registered === 'true') {
      this.$bvToast.toast('Te has registrado correctamente, accede con tus credenciales', {
        title: 'Registro completado',
        variant: 'success',
        solid: true,
        autoHideDelay: 10000,
      });
    }

    this.isLoading = false;
  },

  methods: {
    redirectToMainPage() {
      const redirectUrl = this.$cookies.get('redirectAfterLogin');
      if (redirectUrl) {
        this.$cookies.remove('redirectAfterLogin');
        window.location.href = redirectUrl;
        return;
      }

      // Construct the URL for the main application page
      const vueAppUrl = process.env.VUE_APP_APP_URL_1 || 'http://localhost:8080';
      let destinationPath = '';
      
      // Only add the community slug path segment if it's a valid string
      if (this.communitySlug && this.communitySlug !== '' && this.communitySlug !== 'undefined' && this.communitySlug !== 'null') {
        // Ensure communitySlug is a string
        const validSlug = typeof this.communitySlug === 'object' ? 
          (this.communitySlug.toString() || '') : 
          this.communitySlug;
          
        // Only add path if the slug is not empty
        if (validSlug.trim() !== '') {
          destinationPath = `/${validSlug}`;
        }
      }

      const destinationURL = this.getValidPath(vueAppUrl + destinationPath);
      console.log(`Login - Redirecting to main page: ${destinationURL}`);
      
      window.location.href = destinationURL;
    },

    translate(field) {
      return translateTranslationTable(this.$store.state.locale.currentLocale, field);
    },

    async loadOAuthAndCustomization() {
      // console.log('Login.vue - loadOAuthAndCustomization...');
      try {
        // Guard against no community data
        if (!this.communityData) {
          // console.log('[Debug] No community data available yet, skipping customization');
          return;
        }

        // console.log('[Debug] Applying customizations with community data:', this.communityData);

        // Apply community customizations
        if (this.communityData?.customization?.theme) {
          const customization = this.communityData.customization.theme;
          if (customization.primaryColor) {
            document.documentElement.style.setProperty(
              '--primary-color',
              customization.primaryColor
            );
            let hsl = customization.primaryColor;
            hsl = hsl.substring(4, hsl.length - 1);
            hsl = hsl.split(',');
            document.documentElement.style.setProperty('--primary-color-h', hsl[0]);
            document.documentElement.style.setProperty('--primary-color-s', hsl[1]);
            document.documentElement.style.setProperty('--primary-color-l', hsl[2]);
          }
          if (customization.secondaryColor) {
            document.documentElement.style.setProperty(
              '--secondary-color',
              customization.secondaryColor
            );
            let hslSecondary = customization.secondaryColor;
            hslSecondary = hslSecondary.substring(4, hslSecondary.length - 1);
            hslSecondary = hslSecondary.split(',');
            document.documentElement.style.setProperty('--secondary-color-h', hslSecondary[0]);
            document.documentElement.style.setProperty('--secondary-color-s', hslSecondary[1]);
            document.documentElement.style.setProperty('--secondary-color-l', hslSecondary[2]);
          }
        }

        // Get OAuth apps data using the injected authApiUrl
        const oauth = await this.$store.$service[Service.BackendClient].get(
          'authApps',
          {
            params: { communitySlug: this.communitySlug }
          }
        );
        this.oAuthApps = oauth.data;

        // Get SSO URL if needed
        if (this.communitySlug === 'barcelona-activa') {
          const ssoResponse = await this.$store.$service[Service.BackendClient].get(
            'login-msal',
            {
              params: { communitySlug: this.communitySlug }
            }
          );
          this.ssoURL = ssoResponse.data.url;
        }

        if(this.isCoeicValue){
          this.ssoURL = `/${this.communitySlug}/auth/sso-login`;
        }

        // Set metadata
        if (this.communityData.headMeta) {
          const metadata = this.communityData.headMeta;
          document.getElementsByName('author').content = metadata.name_author;
          document.getElementsByName('description').content = metadata.name_description;
          document.getElementsByName('keywords').content = metadata.name_keywords;
          document.getElementsByName('twitter:title').content = metadata.name_twitter_title;
          document.getElementsByName('twitter:description').content = metadata.name_twitter_description;
          document.getElementById('ogtitle').content = metadata.property_og_title;
          document.getElementById('ogdescription').content = metadata.property_og_description;
        }

        // Set locale
        if (this.communityData.language) {
          this.$store.dispatch('setLocale', this.communityData.language);
        } else {
          if(this.isNexos()){
            this.$store.dispatch('setLocale', 'es');
          } else {
            this.$store.dispatch('setLocale', 'en');
          }
        }

      } catch (error) {
        console.error('Error loading OAuth and customization data:', error);
        this.error = error.message || this.$t('auth.login.error');
      }
    },

    validateForm() {
      // Basic validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      this.invalid = !this.formData.email || 
                     !this.formData.password || 
                     !emailRegex.test(this.formData.email) ||
                     this.formData.password.length < 6
    },

    // Add a helper method to ensure valid paths
    getValidPath(path) {
      if (typeof path === 'object') {
        console.error('Invalid path object:', path);
        return '/';
      }
      return path;
    },

    resetRecaptcha() {
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
        this.captchaVerified = false;
        this.captchaToken = '';
      }
    },

    async handleSubmit() {
      const endpoint = this.authApiUrl + 'login';

      try {
        // Request storage access for cross-site cookies if needed
        if (document.hasStorageAccess && typeof document.requestStorageAccess === 'function') {
          try {
            // Check if we already have storage access
            const hasAccess = await document.hasStorageAccess();
            if (!hasAccess) {
              // Request storage access
              await document.requestStorageAccess();
            }
          } catch (storageError) {
            console.warn('Storage access request failed:', storageError);
            // Continue anyway as it might work without explicit permission
          }
        }

        // Get recaptcha token
        let recaptchaToken = '';
        if (this.$refs.recaptcha) {
          recaptchaToken = this.captchaToken || '';
        }

        const response = await fetch(
          endpoint,
          {
            method: 'POST',
            body: JSON.stringify({
              ...this.formData,
              communitySlug: this.communitySlug,
              recaptchaToken
            }),
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include' // Ensure cookies are sent
          }
        )
        // console.log('login response', response);

        // Clone the response before reading it
        const responseClone = response.clone();

        // Add error handling for non-200 responses
        if (!response.ok) {
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message;
          } catch (jsonError) {
            // If response is not JSON, use the cloned response to get text
            errorMessage = await responseClone.text();
          }
          throw new Error(errorMessage || this.$t('auth.login.error'));
        }

        // const token = await response.json();
        // console.log('login response', response);

        // Check the content type of the response
        const contentType = response.headers.get('content-type');
        let loginResponse;
        
        if (contentType && contentType.includes('application/json')) {
          // Handle JSON response
          loginResponse = await response.json();
        } else {
          // Handle plain text response (Nectios clients with different domain)
          loginResponse = await response.text();
        }
        await this.handleAuthSuccess(loginResponse);
      } catch (error) {
        console.error('Login error:', error);
        this.error = error.message || this.$t('auth.login.error');
        this.resetRecaptcha();
      }
    },

    onCaptchaVerified(response) {
      this.captchaVerified = true;
      this.captchaToken = response;
    },

    onCaptchaExpired() {
      this.captchaVerified = false;
      this.captchaToken = '';
    },
    isExternal(link) {
      return typeof link === 'string' && (link.startsWith('http://') || link.startsWith('https://'));
    },
    trackLinkClick(nombreFlujo, elemento, ubicacion = 'login') {
      return trackLinkClick(nombreFlujo, elemento, ubicacion);
    },
    trackButtonClick(nombreFlujo, elemento, ubicacion = 'login', modal) {
      return trackButtonClick(nombreFlujo, elemento, ubicacion, modal);
    },
    async handleSAMLLogin() {
      if (typeof window === 'undefined') {
        console.error('SAML login is only available on the client side');
        this.error = this.$t('auth.login.saml.error.client');
        return;
      }

      if (!this.communitySlug) {
        console.error('Community slug is required for SAML login');
        this.error = this.$t('auth.login.saml.error.community');
        return;
      }

      this.isSAMLLoading = true;
      this.error = null;

      try {
        // Call the SAML login API endpoint
        const endpoint = this.authApiUrl + 'saml/login';
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ communitySlug: this.communitySlug }),
          credentials: 'include', // Ensure cookies are sent
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'SAML login failed' }));
          throw new Error(errorData.error || errorData.message || 'SAML login failed');
        }

        const data = await response.json();
        
        // SAML login typically returns a redirect URL to the identity provider
        // The response might contain: redirectUrl, authUrl, samlRequest, or similar
        if (data.redirectUrl || data.authUrl || data.url) {
          const redirectUrl = data.redirectUrl || data.authUrl || data.url;
          window.location.href = redirectUrl;
        } else if (data.samlRequest) {
          // If we get SAML request data, we might need to submit a form
          // For now, log it and let the backend handle it
          console.warn('SAML request data received, but redirect URL not found:', data);
          throw new Error('SAML redirect URL not found in response');
        } else {
          console.error('Unexpected SAML login response:', data);
          throw new Error('Invalid SAML login response');
        }
      } catch (error) {
        console.error('SAML login error:', error);
        this.error = error.message || this.$t('auth.login.saml.error.generic');
        this.isSAMLLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login-logo {
  width: 120px;
  margin: auto;
  display: block;
}

.brand {
  height: 32px;
}

.input-group-merge {
  display: flex;
  position: relative;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;

  .form-control-merge {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .input-group-append {
    display: flex;
    margin-left: -1px;

    .input-group-text {
      display: flex;
      align-items: center;
      padding: 0.571rem 1rem;
      margin-bottom: 0;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.45;
      color: #6e6b7b;
      text-align: center;
      white-space: nowrap;
      background-color: #f8f8f8;
      border: 1px solid #d8d6de;
      border-left: 0;
      border-top-right-radius: 0.357rem;
      border-bottom-right-radius: 0.357rem;
    }
  }

  .toggle-password-icon {
    cursor: pointer;
  }
}
</style>
